import {useMemo} from 'react';

import {useCurrentValueInputTypingStore} from 'presentation/components/Filter/store/useCurrentValueInputTypingStore';
import {useGetHealthAvailableTimesQuery} from 'presentation/modules/booking/shared/queries/useGetHealthAvailableTimesQuery';
import {AvailableTimeItem} from 'presentation/modules/booking/shared/queries/useGetHealthMultiAvailableTimesQuery/types';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useQueryParams} from 'presentation/pages';
import {isUnprocessableError} from 'presentation/utils/isUnprocessableError';
import {textHandle} from 'presentation/utils/textHandle';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';
import {UseTelehealthSelectedDateProfessionalListProps} from './types';

export function useTelehealthSelectedDateProfessionalList({
	filteredProvidersNameOptions,
}: UseTelehealthSelectedDateProfessionalListProps) {
	const {
		SEARCH_PARAMS: {query, autocompleteQuery},
	} = useQueryParams();
	const bookingData = useBookingStore(state => state.bookingData);
	const currentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.currentValueInputTyping,
	);
	const setCurrentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.setCurrentValueInputTyping,
	);

	const selectedDate = bookingData.dateAt;
	const normalizedQuery = textHandle(query).sanitizeText();
	const queryToFilter = normalizedQuery === 'clear' ? '' : normalizedQuery;

	const {
		data: healthAvailableTimes,
		isFetching: isFetchingHealthAvailableTimes,
		error: errorHealthAvailableTimes,
		refetch: healthAvailableTimesRefetch,
	} = useGetHealthAvailableTimesQuery({
		isTelemedicine: true,
		enabled: true,
	});

	const notFoundHealthAvailableTimesError = isUnprocessableError(errorHealthAvailableTimes);

	const healthAvailableTimesFiltered = useMemo(() => {
		if (!autocompleteQuery) {
			if (!queryToFilter) return healthAvailableTimes;

			return healthAvailableTimes?.filter(({professionalName}) =>
				textHandle(professionalName).sanitizeText().includes(queryToFilter),
			);
		}

		return healthAvailableTimes;
	}, [healthAvailableTimes, queryToFilter, autocompleteQuery]) as AvailableTimeItem[];

	const hasFilteredOptions = filteredProvidersNameOptions.length > 0;

	const showNotFoundState =
		healthAvailableTimesFiltered?.length === 0 ||
		notFoundHealthAvailableTimesError ||
		(!hasFilteredOptions && currentValueInputTypingState);

	const isError = errorHealthAvailableTimes && !notFoundHealthAvailableTimesError;
	const isLoading = isFetchingHealthAvailableTimes;

	const shouldNotUseHighlight = Boolean(autocompleteQuery || hasFilteredOptions);

	const {isEnabled: isPreviousProviderEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_PREVIOUS_PROVIDER,
	);

	const shouldSkipPreviousProvider = !isPreviousProviderEnabled || Boolean(autocompleteQuery);

	const previousProviderData = useMemo(() => {
		if (shouldSkipPreviousProvider) return null;

		const provider = healthAvailableTimesFiltered?.find(t => t.isPreviousProvider);
		if (!provider || !provider.availableTimes.length) return null;

		return {
			professionalName: provider.professionalName,
			professionalCode: provider.professionalCode,
			recommendations: provider.recommendations,
			isFavorite: provider.isFavorite,
			dates: [{date: selectedDate ?? '', availableTimes: provider.availableTimes}],
		};
	}, [healthAvailableTimesFiltered, shouldSkipPreviousProvider, selectedDate]);

	const timesWithoutPreviousProvider = useMemo(() => {
		if (shouldSkipPreviousProvider) return healthAvailableTimesFiltered;
		return healthAvailableTimesFiltered?.filter(t => !t.isPreviousProvider);
	}, [healthAvailableTimesFiltered, shouldSkipPreviousProvider]);

	const showPreviousProvider = !shouldSkipPreviousProvider && previousProviderData !== null;

	return {
		isLoading,
		isError,
		healthAvailableTimesRefetch,
		showNotFoundState,
		selectedDate,
		setCurrentValueInputTypingState,
		healthAvailableTimesFiltered,
		query,
		shouldNotUseHighlight,
		previousProviderData,
		timesWithoutPreviousProvider,
		showPreviousProvider,
	};
}
